import React, { useEffect, useMemo, useState } from "react";

// Firebase
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import { COLLECTION_NAME } from "../../constants/endpoints";

// Library
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Loader from "../../Components/Loader/Loader";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const formatCurrency = (value) => `$${Number(value || 0).toFixed(2)}`;

const toMonthKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

// Rides created before this cutoff predate the driver/swyft split fields,
// so their `swyftPrice` cannot be derived from retained `price`.
const SWYFT_SPLIT_CUTOFF_TIMESTAMP = new Date("2025-11-01T00:00:00").getTime();

const IncomePage = () => {
  const [selectedMonth, setSelectedMonth] = useState("");
  const [incomeData, setIncomeData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const ridesSnapshot = await getDocs(
          collection(db, COLLECTION_NAME.ridesHistory)
        );
        const paymentsSnapshot = await getDocs(
          collection(db, COLLECTION_NAME.payments)
        );

        const ridesData = ridesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        const paymentsData = paymentsSnapshot.docs.map((doc) => doc.data());
        const rideHistoryIds = new Set(ridesData.map((ride) => ride.id));
        const paymentsLinkedToRideHistory = paymentsData.filter((payment) =>
          rideHistoryIds.has(payment?.rideId)
        );

        const combinedData = processIncomeData(
          ridesData,
          paymentsLinkedToRideHistory
        );
        setIncomeData(combinedData);
        if (combinedData.length > 0) {
          setSelectedMonth(combinedData[combinedData.length - 1].monthKey);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Helper function to convert various timestamp formats to milliseconds
  const getTimestampInMillis = (timestamp) => {
    if (timestamp == null) return null;

    if (timestamp instanceof Date) {
      const time = timestamp.getTime();
      return Number.isNaN(time) ? null : time;
    }

    if (typeof timestamp === "string") {
      const parsed = Date.parse(timestamp);
      return Number.isNaN(parsed) ? null : parsed;
    }

    if (typeof timestamp === "number") {
      return timestamp >= 10000000000 ? timestamp : timestamp * 1000;
    }

    if (typeof timestamp === "object") {
      if (typeof timestamp.toMillis === "function") {
        const millis = timestamp.toMillis();
        return Number.isFinite(millis) ? millis : null;
      }
      if (typeof timestamp.seconds === "number") {
        return timestamp.seconds * 1000;
      }
      if (typeof timestamp._seconds === "number") {
        return timestamp._seconds * 1000;
      }
    }

    return null;
  };

  const getRideEffectiveTimestampMillis = (ride) => {
    const isCompleted =
      String(ride?.status || "").toLowerCase() === "completed" ||
      String(ride?.socketStatus || "").toLowerCase() === "completed";
    const isCancelled =
      "cancellationStatus" in (ride || {}) || !!ride?.cancellationStatus;

    if (isCompleted) {
      const completedTimestamp = getTimestampInMillis(ride?.updatedAt);
      if (completedTimestamp !== null) return completedTimestamp;
    }

    if (isCancelled) {
      const cancelledTimestamp =
        getTimestampInMillis(ride?.cancelledAt) ||
        getTimestampInMillis(ride?.cancelledAtMillis);
      if (cancelledTimestamp !== null) return cancelledTimestamp;
    }

    return (
      getTimestampInMillis(ride?.createdAtMillis) ||
      getTimestampInMillis(ride?.updatedAtMillis) ||
      getTimestampInMillis(ride?.createdAt) ||
      getTimestampInMillis(ride?.updatedAt) ||
      null
    );
  };

  const getNumericValue = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const getDriverShareAmount = (ride) => {
    const driverShare = getNumericValue(ride?.driverPrice);
    return driverShare !== null ? driverShare : 0;
  };

  const isLegacyRide = (ride) => {
    const ts = getRideEffectiveTimestampMillis(ride);
    return ts !== null && ts < SWYFT_SPLIT_CUTOFF_TIMESTAMP;
  };

  // Mirror RideHistory: prefer stored `swyftPrice`; for non-legacy rides without
  // it, fall back to retained `price - driverPrice` so both screens agree.
  const getSwyftShareAmount = (ride) => {
    const storedSwyftShare = getNumericValue(ride?.swyftPrice);
    if (storedSwyftShare !== null) return storedSwyftShare;

    if (isLegacyRide(ride)) return null;

    const retainedPrice = getNumericValue(ride?.price);
    const driverShare = getNumericValue(ride?.driverPrice) || 0;
    if (retainedPrice === null) return null;

    return retainedPrice - driverShare;
  };

  const processIncomeData = (ridesData, paymentsData) => {
    const monthlyMap = {};
    const rideMonthMap = {};
    const rideFinancialMap = {};
    const rideRefundedAmountMap = {};
    const ridePaymentTotalsMap = {};

    paymentsData.forEach((payment) => {
      const rideId = payment?.rideId;
      if (!rideId) return;

      const amount = Math.abs(Number(payment?.amount || 0));
      const paymentType = String(payment?.type || "").toUpperCase();
      if (!Number.isFinite(amount) || amount <= 0) return;

      if (!ridePaymentTotalsMap[rideId]) {
        ridePaymentTotalsMap[rideId] = {
          payments: 0,
          refunds: 0,
        };
      }

      if (paymentType === "PAYMENT") {
        ridePaymentTotalsMap[rideId].payments += amount;
      } else if (paymentType === "REFUND") {
        ridePaymentTotalsMap[rideId].refunds += amount;
      }
    });

    ridesData.forEach((ride) => {
      const isCompleted =
        String(ride?.status || "").toLowerCase() === "completed" ||
        String(ride?.socketStatus || "").toLowerCase() === "completed";
      const isCancelled =
        "cancellationStatus" in (ride || {}) || !!ride?.cancellationStatus;
      if (!isCompleted && !isCancelled) return;

      const timestampMillis = getRideEffectiveTimestampMillis(ride);
      if (timestampMillis === null) return;

      const date = new Date(timestampMillis);
      const monthKey = toMonthKey(date);
      const monthLabel = `${MONTHS[date.getMonth()]} ${date.getFullYear()}`;

      if (!monthlyMap[monthKey]) {
        monthlyMap[monthKey] = {
          month: MONTHS[date.getMonth()],
          monthNumber: date.getMonth() + 1,
          year: date.getFullYear(),
          monthKey,
          monthLabel,
          driverIncome: 0,
          swyftShare: 0,
          totalIncome: 0,
          totalPayments: 0,
          totalRefunds: 0,
        };
      }

      rideMonthMap[ride.id] = monthKey;

      const driverIncome = getDriverShareAmount(ride);
      const rideTip = Math.max(0, Number(ride?.tip || 0));
      const computedSwyft = getSwyftShareAmount(ride);
      const ridePaymentTotals = ridePaymentTotalsMap[ride.id] || {
        payments: 0,
        refunds: 0,
      };
      const paymentBackedIncome = Math.max(
        ridePaymentTotals.payments - ridePaymentTotals.refunds,
        0
      );
      // Label is "Driver Share (Including Tips)" — always add `tip` to driverPrice.
      const safeDriverIncome = driverIncome + rideTip;
      let safeSwyftIncome =
        computedSwyft !== null && computedSwyft > 0 ? computedSwyft : 0;
      if (safeDriverIncome + safeSwyftIncome === 0 && paymentBackedIncome > 0) {
        // Legacy / thin records: no driverPrice+swyftPrice but net payments exist
        // (e.g. cancellations) — attribute net to Swyft; do not use retained `price`.
        safeSwyftIncome = paymentBackedIncome;
      }
      const rideBaseAmount = Math.max(
        safeDriverIncome + safeSwyftIncome,
        paymentBackedIncome
      );

      monthlyMap[monthKey].totalIncome += safeDriverIncome + safeSwyftIncome;
      monthlyMap[monthKey].driverIncome += safeDriverIncome;
      monthlyMap[monthKey].swyftShare += safeSwyftIncome;

      rideFinancialMap[ride.id] = {
        monthKey,
        baseAmount: rideBaseAmount,
        driverIncome: safeDriverIncome,
        swyftShare: safeSwyftIncome,
      };
    });

    paymentsData.forEach((payment) => {
      const monthKey = rideMonthMap[payment?.rideId];
      if (!monthKey || !monthlyMap[monthKey]) return;

      const amount = Number(payment?.amount || 0);
      const paymentType = String(payment?.type || "").toUpperCase();
      const normalizedAmount = Math.abs(amount);

      if (paymentType === "PAYMENT") {
        monthlyMap[monthKey].totalPayments += normalizedAmount;
      } else if (paymentType === "REFUND") {
        monthlyMap[monthKey].totalRefunds += normalizedAmount;
      }
    });

    return Object.values(monthlyMap).sort((a, b) =>
      a.monthKey.localeCompare(b.monthKey)
    );
  };

  const selectedMonthKey = useMemo(() => {
    if (!selectedMonth) return "";
    return selectedMonth;
  }, [selectedMonth]);

  const selectedYear = useMemo(() => selectedMonthKey.slice(0, 4), [selectedMonthKey]);

  const selectedMonthNumber = useMemo(
    () => Number(selectedMonthKey.slice(5, 7)),
    [selectedMonthKey]
  );

  const yearOptions = useMemo(
    () =>
      [...new Set(incomeData.map((item) => item.year))]
        .sort((a, b) => b - a)
        .map(String),
    [incomeData]
  );

  const monthOptionsForYear = useMemo(
    () =>
      incomeData
        .filter((item) => String(item.year) === selectedYear)
        .sort((a, b) => a.monthNumber - b.monthNumber),
    [incomeData, selectedYear]
  );

  const handleYearChange = (nextYear) => {
    const monthsInYear = incomeData
      .filter((item) => String(item.year) === nextYear)
      .sort((a, b) => b.monthNumber - a.monthNumber);

    if (monthsInYear.length > 0) {
      setSelectedMonth(monthsInYear[0].monthKey);
    }
  };

  const handleMonthChange = (monthNumber) => {
    if (!selectedYear || !monthNumber) return;
    setSelectedMonth(`${selectedYear}-${String(monthNumber).padStart(2, "0")}`);
  };

  const selectedMonthData = useMemo(() => {
    const fallback = {
      month: MONTHS[new Date().getMonth()],
      year: new Date().getFullYear(),
      monthKey: selectedMonthKey,
      monthLabel: "Selected Month",
      driverIncome: 0,
      swyftShare: 0,
      totalIncome: 0,
    };

    if (!selectedMonthKey) return fallback;

    return (
      incomeData.find((data) => data.monthKey === selectedMonthKey) || fallback
    );
  }, [incomeData, selectedMonthKey]);

  const pieData = useMemo(
    () => [
      { name: "Driver Share (Including Tips)", value: selectedMonthData.driverIncome },
      { name: "Swyft Share", value: selectedMonthData.swyftShare },
    ],
    [selectedMonthData]
  );

  const swyftComparisonData = useMemo(
    () =>
      incomeData.map((item) => ({
        monthLabel: item.monthLabel,
        swyftShare: Number(item.swyftShare.toFixed(2)),
      })),
    [incomeData]
  );

  const bestSwyftMonth = useMemo(() => {
    if (incomeData.length === 0) return null;
    return incomeData.reduce((best, current) =>
      current.swyftShare > best.swyftShare ? current : best
    );
  }, [incomeData]);

  const COLORS = ["#47BFCF", "#27679C"];

  const renderCustomLabel = ({ value }) => {
    if (!value) return "$0.00";
    return formatCurrency(value);
  };

  return (
    <div className="px-1 sm:px-1 md-px-4 min-h-screen">
      <h1 className="text-2xl md:text-3xl font-bold mt-8 md:mt-5 mb-2 md:mb-5 text-primary">
        My Income
      </h1>

      {loading ? (
        <Loader />
      ) : (
        <div className="space-y-6">
          <div className="bg-slate-100 p-4 rounded-lg shadow-sm">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-4 mx-2">
              <h2 className="text-xl font-bold text-gray-700">
                Income Overview
              </h2>
              <div className="flex items-center gap-3">
                <label className="text-gray-700 font-semibold">
                  Calendar Filter:
                </label>
                <select
                  className="bg-white border-2 border-gray-100 p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  value={selectedYear}
                  onChange={(e) => handleYearChange(e.target.value)}
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
                <select
                  className="bg-white border-2 border-gray-100 p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  value={selectedMonthNumber || ""}
                  onChange={(e) => handleMonthChange(e.target.value)}
                >
                  {monthOptionsForYear.map((monthData) => (
                    <option key={monthData.monthKey} value={monthData.monthNumber}>
                      {monthData.month}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="text-sm text-gray-600">Total Income</p>
                <p className="text-xl font-bold text-primary">
                  {formatCurrency(selectedMonthData.totalIncome)}
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="text-sm text-gray-600">
                  Driver Share (Including Tips)
                </p>
                <p className="text-xl font-bold text-cyan-600">
                  {formatCurrency(selectedMonthData.driverIncome)}
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="text-sm text-gray-600">Swyft Share</p>
                <p className="text-xl font-bold text-blue-700">
                  {formatCurrency(selectedMonthData.swyftShare)}
                </p>
              </div>
            </div>

            {selectedMonthData.totalIncome === 0 ? (
              <div className="text-center text-red-700">
                No income data for selected month.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={360}>
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={130}
                    innerRadius={75}
                    label={renderCustomLabel}
                  >
                    {pieData.map((entry, index) => (
                      <Cell
                        key={`cell-${entry.name}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="bg-slate-100 p-4 rounded-lg shadow-sm">
            <h2 className="text-xl font-bold mb-3 mx-2 text-gray-700">
              Swyft Share Monthly Comparison
            </h2>
            {bestSwyftMonth && (
              <p className="mx-2 mb-4 text-gray-700">
                Highest swyft share: <strong>{bestSwyftMonth.monthLabel}</strong>{" "}
                with <strong>{formatCurrency(bestSwyftMonth.swyftShare)}</strong>
              </p>
            )}

            {swyftComparisonData.length === 0 ? (
              <div className="text-center text-red-700">
                No monthly swyft share data available.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={swyftComparisonData}
                  margin={{ top: 10, right: 20, left: 0, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="monthLabel"
                    angle={-35}
                    textAnchor="end"
                    interval={0}
                    height={80}
                  />
                  <YAxis />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Bar dataKey="swyftShare" name="Swyft Share" fill="#27679C" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default IncomePage;

